import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const FirmaExitosa = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { documento, firmante, fechaFirma, hash_documento, download_url, firma_id, documento_id } = location.state || {};
  
  // Construir URL de descarga
  const getDownloadUrl = () => {
    // Prioridad 1: Si viene download_url del resultado
    if (download_url) {
      // Si viene la URL completa, usarla directamente
      if (download_url.startsWith('http')) {
        return download_url;
      }
      // Si es relativa, asegurar que tenga /api
      let cleanUrl = download_url;
      if (cleanUrl.startsWith('/api/')) {
        // Ya tiene /api, usar directamente
        return `${API_BASE_URL}${cleanUrl}`;
      } else if (cleanUrl.startsWith('/')) {
        // Empieza con / pero no tiene /api, agregarlo
        return `${API_BASE_URL}/api${cleanUrl}`;
      } else {
        // No empieza con /, agregar /api/
        return `${API_BASE_URL}/api/${cleanUrl}`;
      }
    }
    
    // Prioridad 2: Si tenemos firma_id, usar el endpoint de descarga por firma_id
    if (firma_id) {
      return `${API_BASE_URL}/api/download/${firma_id}`;
    }
    
    // Prioridad 3: Si tenemos hash_documento, usar el endpoint público
    if (hash_documento) {
      return `${API_BASE_URL}/api/documentos/download/${hash_documento}`;
    }
    
    // Prioridad 4: Si tenemos documento con hash
    if (documento?.hash_documento) {
      return `${API_BASE_URL}/api/documentos/download/${documento.hash_documento}`;
    }
    
    return null;
  };
  
  const downloadUrl = getDownloadUrl();
  
  // Debug: mostrar información en consola
  React.useEffect(() => {
    console.log('[FirmaExitosa] Estado recibido:', {
      hash_documento,
      download_url,
      firma_id,
      documento_id,
      documento,
      downloadUrl,
      API_BASE_URL
    });
  }, [hash_documento, download_url, firma_id, documento_id, documento, downloadUrl]);
  
  const handleDownload = () => {
    if (downloadUrl) {
      console.log('[FirmaExitosa] Descargando desde:', downloadUrl);
      window.open(downloadUrl, '_blank');
    } else {
      console.error('[FirmaExitosa] No hay URL de descarga disponible');
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'Fecha no disponible';
    return new Date(fecha).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {/* Icono de éxito */}
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
          <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        {/* Título */}
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          ¡Firma Completada!
        </h1>

        {/* Información del documento */}
        {documento && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-gray-800 mb-2">Documento:</h3>
            <p className="text-gray-600 text-sm mb-1">
              <strong>Título:</strong> {documento.titulo}
            </p>
            {documento.descripcion && (
              <p className="text-gray-600 text-sm mb-1">
                <strong>Descripción:</strong> {documento.descripcion}
              </p>
            )}
            <p className="text-gray-600 text-sm">
              <strong>Hash:</strong> {documento.hash_documento?.substring(0, 16)}...
            </p>
          </div>
        )}

        {/* Información del firmante */}
        {firmante && (
          <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-gray-800 mb-2">Firmante:</h3>
            <p className="text-gray-600 text-sm mb-1">
              <strong>Nombre:</strong> {firmante.nombre} {firmante.apellido}
            </p>
            <p className="text-gray-600 text-sm mb-1">
              <strong>Email:</strong> {firmante.email}
            </p>
            <p className="text-gray-600 text-sm">
              <strong>Fecha de firma:</strong> {formatFecha(fechaFirma)}
            </p>
          </div>
        )}

        {/* Mensaje de confirmación */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-green-800 text-sm">
            <strong>✅ Confirmación:</strong> Tu firma ha sido procesada exitosamente y 
            agregada al documento. El documento firmado está disponible para descarga.
          </p>
        </div>

        {/* Información adicional */}
        <div className="text-sm text-gray-600 mb-6">
          <p className="mb-2">
            <strong>¿Qué sigue?</strong>
          </p>
          <ul className="text-left space-y-1">
            <li>• El documento firmado se ha guardado de forma segura</li>
            <li>• Se ha enviado una notificación al propietario del documento</li>
            <li>• Puedes cerrar esta ventana de forma segura</li>
          </ul>
        </div>

        {/* Botones de acción */}
        <div className="space-y-3">
          {downloadUrl && (
            <button
              onClick={handleDownload}
              className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2 font-semibold"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              📥 Descargar Documento Firmado
            </button>
          )}
          
          <button
            onClick={() => navigate('/documentos-pendientes')}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Ir a Mis Pendientes
          </button>
          
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
          >
            Volver
          </button>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Sistema de Firmas Digitales - {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FirmaExitosa;


